import axios from 'axios';
import { API_BASE_URL } from '../api/config';

/**
 * Formats Velavan Finance ledger records
 */
export const formatVelavanFinanceData = (entries = []) => {
  return entries.map((entry) => ({
    'Ref No': entry.refNo || '',
    'Date': entry.date || '',
    'Customer Name': entry.name || '',
    'Location': entry.location || '',
    'Contact 1': entry.contactNo1 || '',
    'Contact 2': entry.contactNo2 || '',
    'Aadhar No 1': entry.aadharNo1 || '',
    'Aadhar No 2': entry.aadharNo2 || '',
    'Occupation': entry.occupation || '',
    'Lended Amount (Rs)': Number(entry.lendedAmount) || 0,
    'Weekly Due (Rs)': Number(entry.weeklyDue) || 0,
    'Total Dues': Number(entry.totalDues) || 0,
    'Dues Paid': Number(entry.noOfDuePaid) || 0,
    'Remaining Dues': Number(entry.remainingDues ?? Math.max(0, (Number(entry.totalDues) || 0) - (Number(entry.noOfDuePaid) || 0))),
    'Pending Due': Number(entry.pendingDue) || 0,
    'Account Status': (entry.accountStatus || 'Active').toUpperCase(),
  }));
};

/**
 * Formats Due Matters records
 */
export const formatDueMattersData = (entries = []) => {
  return entries.map((entry) => ({
    'Ref No': entry.refNo || '',
    'Date': entry.date || '',
    'Customer Name': entry.name || '',
    'Location': entry.location || '',
    'Contact Number': entry.contactNo1 || '',
    'Lended Capital (Rs)': Number(entry.lendedAmount) || 0,
    'Weekly Due (Rs)': Number(entry.weeklyDue) || 0,
    'Total Dues': Number(entry.totalDues) || 0,
    'Dues Paid': Number(entry.noOfDuePaid) || 0,
    'Remaining Dues': Number(entry.remainingDues ?? Math.max(0, (Number(entry.totalDues) || 0) - (Number(entry.noOfDuePaid) || 0))),
    'Pending Due Count': Number(entry.pendingDue) || 0,
    'Principal In Due (Rs)': Number(entry.totalPrincipalDue) || 0,
    'Interest In Due (Rs)': Number(entry.totalInterestDue) || 0,
    'Due Amount Paid (Rs)': Number(entry.paidDueAmount) || 0,
    'Remaining Principal (Rs)': Number(entry.remainingPrincipalTobePaid) || 0,
    'Account Status': (entry.accountStatus || 'Active').toUpperCase(),
  }));
};

/**
 * Formats Interest Calculations records
 */
export const formatInterestCalculationsData = (entries = []) => {
  return entries.map((entry) => ({
    'Ref No': entry.refNo || '',
    'Date': entry.date || '',
    'Customer Name': entry.name || '',
    'Lended Capital (Rs)': Number(entry.lendedAmount) || 0,
    'Weekly Due (Rs)': Number(entry.weeklyDue) || 0,
    'Total Dues': Number(entry.totalDues) || 0,
    'Dues Paid': Number(entry.noOfDuePaid) || 0,
    'Remaining Dues': Number(entry.remainingDues) || 0,
    'Pending Due': Number(entry.pendingDue) || 0,
    'Principal In Due (Rs)': Number(entry.totalPrincipalDue) || 0,
    'Interest In Due (Rs)': Number(entry.totalInterestDue) || 0,
    'Amount Paid (Rs)': Number(entry.paidDueAmount) || 0,
    'Principal Due Rem. (Rs)': Number(entry.remainingPrincipalTobePaid) || 0,
    'Net Profit (Rs)': Number(entry.profit) || 0,
  }));
};

/**
 * Formats Daily Accounts records (flattened across all months)
 */
export const formatDailyAccountsData = (summaries = []) => {
  const dailyRows = [];

  summaries.forEach((summary) => {
    const month = summary.summaryMonth || '';
    const entries = summary.dailyEntries || [];

    entries.forEach((daily) => {
      dailyRows.push({
        'Month/Year': month,
        'Date': daily.date || '',
        'Opening Balance (Rs)': Number(daily.openingBalance) || 0,
        'Fund Released to VF (Rs)': Number(daily.fundReleasedToF2F) || 0,
        'Number of Loans': Number(daily.noOfLoans) || 0,
        'Loan Amount (Rs)': Number(daily.loanAmount) || 0,
        'Principal In Due (Rs)': Number(daily.principalInDue) || 0,
        'Interest In Due (Rs)': Number(daily.interestInDue) || 0,
        'Due Amount Paid (Rs)': Number(daily.dueAmountPaid ?? ((Number(daily.principalInDue) || 0) + (Number(daily.interestInDue) || 0))),
        'Pending Due': Number(daily.pendingDue) || 0,
        'Refreshment Expenses (Rs)': Number(daily.refreshmentExpenses) || 0,
        'Other Expenses (Rs)': Number(daily.otherExpenses) || 0,
        'Other Expense Purpose': daily.otherExpensePurpose || '-',
        'Documentation Charge (Rs)': Number(daily.docCharge) || 200,
        'Profit Withdrawn (Rs)': Number(daily.profitWithdrawn) || 0,
        'Closing Balance (Rs)': Number(daily.closingBalance) || 0,
      });
    });
  });

  return dailyRows;
};

/**
 * Formats Final Accounts records (monthly / annual rollup summaries)
 */
export const formatFinalAccountsData = (summaries = []) => {
  return summaries.map((summary) => ({
    'Month/Year': summary.summaryMonth || '',
    'Opening Balance (Rs)': Number(summary.monthlyTotals?.openingBalance) || 0,
    'Fund Released to VF (Rs)': Number(summary.monthlyTotals?.totalFundReleasedToF2F) || 0,
    'Fund in Market (Rs)': Number(summary.monthlyTotals?.totalFundInMarket) || 0,
    'Total Loans': Number(summary.monthlyTotals?.totalNoOfLoans) || 0,
    'Total Loan Amount (Rs)': Number(summary.monthlyTotals?.totalLoanAmount) || 0,
    'Total Doc Charges (Rs)': Number(summary.monthlyTotals?.totalDocCharge) || 0,
    'Principal In Due (Rs)': Number(summary.monthlyTotals?.totalPrincipalInDue) || 0,
    'Interest In Due (Rs)': Number(summary.monthlyTotals?.totalInterestInDue) || 0,
    'Total Due Amount Paid (Rs)': Number(summary.monthlyTotals?.totalDueAmountPaid) || 0,
    'Office Rent (Rs)': Number(summary.monthlyTotals?.totalOfficeRent) || 0,
    'Staff Salary (Rs)': Number(summary.monthlyTotals?.totalStaffSalary) || 0,
    'Office Expenses (Rs)': Number(summary.monthlyTotals?.totalOfficeExpenses) || 0,
    'Final Income (Rs)': Number(summary.monthlyTotals?.totalFinalIncome) || 0,
    "Manager's Share (Rs)": Number(summary.monthlyTotals?.totalManagersShare) || 0,
    'Net Profit (Rs)': Number(summary.monthlyTotals?.totalProfit) || 0,
    'Profit Withdrawn (Rs)': Number(summary.monthlyTotals?.totalProfitWithdrawn) || 0,
    'Final Closing Balance (Rs)': Number(summary.monthlyTotals?.totalClosingBalance) || 0,
  }));
};

/**
 * Formats Account Status records
 */
export const formatAccountStatusData = (entries = []) => {
  return entries.map((entry) => ({
    'Ref No': entry.refNo || '',
    'Customer Name': entry.name || '',
    'Location': entry.location || '',
    'Contact Number': entry.contactNo1 || '',
    'Lended Amount (Rs)': Number(entry.lendedAmount) || 0,
    'Weekly Due (Rs)': Number(entry.weeklyDue) || 0,
    'Total Dues': Number(entry.totalDues) || 0,
    'Dues Paid': Number(entry.noOfDuePaid) || 0,
    'Remaining Dues': Number(entry.remainingDues ?? 0),
    'Pending Due': Number(entry.pendingDue ?? 0),
    'Due Amount Paid (Rs)': Number(entry.paidDueAmount ?? 0),
    'Account Status': (entry.accountStatus || 'Active').toUpperCase(),
  }));
};

/**
 * Fetches all collections from the backend database with high resilience
 */
export const fetchAllCollections = async () => {
  // Retrieve token/user context if available
  let headers = {};
  try {
    const storedUser = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed?.token) {
        headers['Authorization'] = `Bearer ${parsed.token}`;
      }
    }
  } catch (e) {
    console.warn('Could not read user auth context:', e);
  }

  // Fetch collections independently using allSettled to prevent single endpoint failures from blocking export
  const [vfResult, accountsResult] = await Promise.allSettled([
    axios.get(`${API_BASE_URL}/velavan_finance/get_all_entries`, { headers, timeout: 15000 }),
    axios.get(`${API_BASE_URL}/accounts/get_all_summaries`, { headers, timeout: 15000 }),
  ]);

  let vfEntries = [];
  let accSummaries = [];
  const errors = [];

  if (vfResult.status === 'fulfilled' && Array.isArray(vfResult.value?.data)) {
    vfEntries = vfResult.value.data;
  } else {
    const reason = vfResult.status === 'rejected' ? (vfResult.reason?.response?.data?.message || vfResult.reason?.message) : 'Invalid data format';
    console.warn('⚠️ Velavan Finance collection fetch warning:', reason);
    errors.push(`Velavan Finance: ${reason}`);
  }

  if (accountsResult.status === 'fulfilled' && Array.isArray(accountsResult.value?.data)) {
    accSummaries = accountsResult.value.data;
  } else {
    const reason = accountsResult.status === 'rejected' ? (accountsResult.reason?.response?.data?.message || accountsResult.reason?.message) : 'Invalid data format';
    console.warn('⚠️ Daily Accounts summaries fetch warning:', reason);
    errors.push(`Daily Accounts: ${reason}`);
  }

  // If both endpoints completely failed with network / server errors, throw descriptive error
  if (vfEntries.length === 0 && accSummaries.length === 0 && errors.length > 0) {
    throw new Error(`Failed to retrieve database collections (${errors.join('; ')}). Please check database connectivity and try again.`);
  }

  return {
    velavanFinance: formatVelavanFinanceData(vfEntries),
    dueMatters: formatDueMattersData(vfEntries),
    interestCalculations: formatInterestCalculationsData(vfEntries),
    dailyAccounts: formatDailyAccountsData(accSummaries),
    finalAccounts: formatFinalAccountsData(accSummaries),
    accountStatus: formatAccountStatusData(vfEntries),
    rawCounts: {
      vfCount: vfEntries.length,
      accCount: accSummaries.length,
      dailyCount: accSummaries.reduce((acc, s) => acc + (s.dailyEntries?.length || 0), 0),
    },
    errors,
  };
};

/**
 * Generates an XML-based multi-sheet SpreadsheetML workbook
 * Compatible with all versions of Microsoft Excel, LibreOffice & Apple Numbers
 */
const generateSpreadsheetMLWorkbook = (sheetsMap) => {
  const escapeXML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  let xml = `<?xml version="1.0" encoding="UTF-8"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:html="http://www.w3.org/TR/REC-html40">
 <DocumentProperties xmlns="urn:schemas-microsoft-com:office:office">
  <Author>Velavan Finance</Author>
  <Created>${new Date().toISOString()}</Created>
  <Company>Velavan Finance Pvt</Company>
 </DocumentProperties>
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal">
   <Alignment ss:Vertical="Center"/>
   <Font ss:FontName="Segoe UI" ss:Size="10" ss:Color="#1e293b"/>
  </Style>
  <Style ss:ID="Header">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#0f766e"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#cbd5e1"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#cbd5e1"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#cbd5e1"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="11" ss:Color="#ffffff" ss:Bold="1"/>
   <Interior ss:Color="#0f766e" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="HeaderPurple">
   <Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="2" ss:Color="#936097"/>
   </Borders>
   <Font ss:FontName="Segoe UI" ss:Size="11" ss:Color="#ffffff" ss:Bold="1"/>
   <Interior ss:Color="#936097" ss:Pattern="Solid"/>
  </Style>
  <Style ss:ID="DataCell">
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
   </Borders>
  </Style>
  <Style ss:ID="NumberCell">
   <Alignment ss:Horizontal="Right"/>
   <Borders>
    <Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Top" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Left" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
    <Border ss:Position="Right" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#f1f5f9"/>
   </Borders>
   <NumberFormat ss:Format="#,##0"/>
  </Style>
 </Styles>
`;

  Object.entries(sheetsMap).forEach(([sheetName, rows], sheetIdx) => {
    const headerStyle = sheetIdx % 2 === 0 ? 'Header' : 'HeaderPurple';
    xml += ` <Worksheet ss:Name="${escapeXML(sheetName)}">\n  <Table ss:DefaultRowHeight="20">\n`;

    if (rows.length > 0) {
      const headers = Object.keys(rows[0]);

      // Column definitions
      headers.forEach(() => {
        xml += `   <Column ss:AutoFitWidth="1" ss:Width="130"/>\n`;
      });

      // Header Row
      xml += `   <Row ss:Height="26">\n`;
      headers.forEach((h) => {
        xml += `    <Cell ss:StyleID="${headerStyle}"><Data ss:Type="String">${escapeXML(h)}</Data></Cell>\n`;
      });
      xml += `   </Row>\n`;

      // Data Rows
      rows.forEach((row) => {
        xml += `   <Row ss:Height="20">\n`;
        headers.forEach((h) => {
          const val = row[h];
          if (typeof val === 'number') {
            xml += `    <Cell ss:StyleID="NumberCell"><Data ss:Type="Number">${val}</Data></Cell>\n`;
          } else {
            xml += `    <Cell ss:StyleID="DataCell"><Data ss:Type="String">${escapeXML(val)}</Data></Cell>\n`;
          }
        });
        xml += `   </Row>\n`;
      });
    } else {
      xml += `   <Row><Cell ss:StyleID="Default"><Data ss:Type="String">No records found</Data></Cell></Row>\n`;
    }

    xml += `  </Table>\n </Worksheet>\n`;
  });

  xml += `</Workbook>`;
  return xml;
};

/**
 * Main export function to generate and download multi-sheet Excel file
 */
export const exportAllCollectionsToExcel = async (onProgress = null) => {
  try {
    if (onProgress) onProgress({ status: 'fetching', message: 'Fetching all collections from database...' });

    const collections = await fetchAllCollections();

    if (onProgress) onProgress({ status: 'generating', message: 'Building multi-sheet Excel workbook...' });

    const sheets = {
      'Velavan Finance': collections.velavanFinance,
      'Due Matters': collections.dueMatters,
      'Interest Calculations': collections.interestCalculations,
      'Daily Accounts': collections.dailyAccounts,
      'Final Accounts': collections.finalAccounts,
      'Account Status': collections.accountStatus,
    };

    const dateStr = new Date().toISOString().split('T')[0];
    const filename = `Velavan_Finance_All_Collections_${dateStr}.xlsx`;

    // Try using dynamic SheetJS / xlsx if available
    let exported = false;
    try {
      const XLSX = await import('xlsx');
      if (XLSX && XLSX.utils) {
        const wb = XLSX.utils.book_new();

        Object.entries(sheets).forEach(([sheetName, data]) => {
          const ws = XLSX.utils.json_to_sheet(data);
          XLSX.utils.book_append_sheet(wb, ws, sheetName);
        });

        XLSX.writeFile(wb, filename);
        exported = true;
      }
    } catch (e) {
      console.warn('XLSX module not available, using high-compatibility multi-sheet SpreadsheetML XML export', e);
    }

    // Fallback: Generate valid SpreadsheetML XML workbook with .xls / .xml compatibility
    if (!exported) {
      const xmlWorkbook = generateSpreadsheetMLWorkbook(sheets);
      const blob = new Blob([xmlWorkbook], {
        type: 'application/vnd.ms-excel;charset=utf-8',
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Velavan_Finance_All_Collections_${dateStr}.xls`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }

    if (onProgress) onProgress({ status: 'success', message: 'Excel workbook downloaded successfully!' });
    return { success: true, collections };
  } catch (error) {
    console.error('Error exporting database collections to Excel:', error);
    if (onProgress) onProgress({ status: 'error', message: error.message || 'Failed to export collections' });
    throw error;
  }
};
